<template>
	<div class="list-field-control" :class="{ 'list-field-control--readonly': field.disabled, 'list-field-control--error': hasError, 'list-field-control--open': menu }">
		<v-menu v-model="menu" :attach="attachTarget" :close-on-content-click="!multiple" :disabled="field.disabled || isTablet" location="bottom start" scroll-strategy="close" :scrim="false" content-class="list-field-control-menu">
			<template #activator="{ props: menuProps }">
				<div v-bind="menuProps" class="list-field-control__cell" :tabindex="field.disabled ? -1 : 0" role="button" :aria-expanded="menu || sheetOpen" :title="display.title || undefined" @click="onCellClick">
					<span v-if="!multiple" class="list-field-control__value">{{ display.text }}</span>
					<template v-else>
						<v-chip v-for="chip in display.chips" :key="chip.id" class="list-field-control__chip" size="x-small" label>{{ chip.label }}</v-chip>
						<v-chip v-if="display.overflowCount" class="list-field-control__chip list-field-control__chip--overflow" size="x-small" label>+{{ display.overflowCount }}</v-chip>
					</template>
					<v-icon v-if="!field.disabled" class="list-field-control__chevron" size="18">{{ menu ? 'expand_less' : 'expand_more' }}</v-icon>
				</div>
			</template>

			<v-sheet class="list-field-control-menu__panel">
				<div v-if="showSearch" class="list-field-control-menu__search" @click.stop>
					<v-text-field v-model="search" :label="t('Search')" density="compact" variant="outlined" prepend-inner-icon="search" clearable hide-details autocomplete="off"></v-text-field>
				</div>

				<v-list class="list-field-control-menu__list" density="compact">
					<v-list-item v-for="option in visibleOptions" :key="option.id" class="list-field-control-menu__option" :active="isSelected(option.id)" @click="pick(option.id)">
						<template #prepend>
							<v-checkbox-btn v-if="multiple" :model-value="isSelected(option.id)" density="compact" :ripple="false" tabindex="-1" />
							<v-icon v-else size="18">{{ isSelected(option.id) ? 'radio_button_checked' : 'radio_button_unchecked' }}</v-icon>
						</template>
						<v-list-item-title class="list-field-control-menu__label">{{ option.label }}</v-list-item-title>
					</v-list-item>
					<v-list-item v-if="!visibleOptions.length" class="list-field-control-menu__empty" disabled>
						<v-list-item-title class="list-field-control-menu__label">{{ t('NoResultForSearch') }}</v-list-item-title>
					</v-list-item>
				</v-list>
			</v-sheet>
		</v-menu>

		<v-bottom-sheet v-model="sheetOpen" :attach="attachTarget" class="list-field-control__sheet-overlay">
			<v-card class="list-field-control__sheet">
				<v-card-title class="list-field-control__sheet-title">
					{{ field.label }}
					<v-spacer></v-spacer>
					<v-btn icon="close" variant="tonal" color="primary" density="comfortable" @click="sheetOpen = false" />
				</v-card-title>

				<div v-if="showSearch" class="list-field-control__sheet-search">
					<v-text-field v-model="search" :label="t('Search')" density="compact" variant="outlined" prepend-inner-icon="search" clearable hide-details autocomplete="off"></v-text-field>
				</div>

				<v-list class="list-field-control__sheet-list" density="comfortable">
					<v-list-item v-for="option in visibleOptions" :key="option.id" class="list-field-control-menu__option" :active="isSelected(option.id)" @click="pick(option.id)">
						<template #prepend>
							<v-checkbox-btn v-if="multiple" :model-value="isSelected(option.id)" density="compact" :ripple="false" tabindex="-1" @click.stop="pick(option.id)" />
							<v-icon v-else size="18">{{ isSelected(option.id) ? 'radio_button_checked' : 'radio_button_unchecked' }}</v-icon>
						</template>
						<v-list-item-title class="list-field-control-menu__label">{{ option.label }}</v-list-item-title>
					</v-list-item>
					<v-list-item v-if="!visibleOptions.length" class="list-field-control-menu__empty" disabled>
						<v-list-item-title class="list-field-control-menu__label">{{ t('NoResultForSearch') }}</v-list-item-title>
					</v-list-item>
				</v-list>
			</v-card>
		</v-bottom-sheet>
	</div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { toggleSelection, buildDisplay, filterOptions, shouldShowSearch } from '@/utils/legacy/listFieldValue';
import { writeNativeField, resolveAttachTarget } from '@/utils/legacy/legacyNativeField';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { useOverlayScrimClose } from '@/composables/useOverlayScrimClose';
import { useTranslator } from '@/composables/useTranslator';

const props = defineProps({
	field: { type: Object, required: true },
});

const t = useTranslator();
const menu = ref(false);
const sheetOpen = ref(false);
const search = ref('');
const showSearch = computed(() => shouldShowSearch(props.field.options.length));
const visibleOptions = computed(() => filterOptions(props.field.options, search.value));
const multiple = computed(() => props.field.type === 5);
const hasError = computed(() => props.field.mandatory && display.value.isEmpty);

const attachTarget = computed(() => {
	const doc = props.field.nativeEl?.ownerDocument;
	return doc ? resolveAttachTarget(props.field.nativeEl, doc) : undefined;
});

const display = computed(() =>
	buildDisplay(props.field.selected, props.field.options, {
		multiple: multiple.value,
		maxDisplayedChips: props.field.maxDisplayedChips,
		itemValue: 'id',
		titleKey: 'label',
	})
);

// On mobile the field's own v-menu is replaced by a v-bottom-sheet (à la SelectControl.vue /
// UserViewControl.vue): the menu goes disabled and a click on the cell opens the sheet instead.
const isTablet = ref(deviceUtils.isTablet());
function onDeviceResize() {
	isTablet.value = deviceUtils.isTablet();
}
onMounted(() => window.addEventListener('resize', onDeviceResize));
onBeforeUnmount(() => window.removeEventListener('resize', onDeviceResize));

const anyOverlayOpen = computed(() => menu.value || sheetOpen.value);

useOverlayScrimClose(
	sheetOpen,
	() => {
		sheetOpen.value = false;
	},
	attachTarget.value,
	'.list-field-control__sheet-overlay'
);

function isSelected(id) {
	return props.field.selected.includes(String(id));
}

function onCellClick() {
	if (props.field.disabled || !isTablet.value) return;
	sheetOpen.value = true;
}

function pick(id) {
	const next = toggleSelection(props.field.selected, id, { multiple: multiple.value, options: props.field.options, itemValue: 'id' });
	props.field.selected = writeNativeField(props.field.nativeEl, next);
	if (multiple.value) return;
	menu.value = false;
	sheetOpen.value = false;
}

function onEscape(event) {
	if (event.key !== 'Escape') return;
	menu.value = false;
	sheetOpen.value = false;
}

watch(anyOverlayOpen, (isOpen) => {
	props.field.menuOpen = isOpen;
	search.value = '';

	const doc = props.field.nativeEl?.ownerDocument;
	if (!doc) return;

	if (isOpen) doc.addEventListener('keydown', onEscape, true);
	else doc.removeEventListener('keydown', onEscape, true);
});

onBeforeUnmount(() => {
	props.field.nativeEl?.ownerDocument?.removeEventListener('keydown', onEscape, true);
});
</script>
